import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Star, Check, Loader2, Plus, Minus } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { PlanType, pricingTiers, getSelectedPlan } from '@/config/pricing';
import { supabase } from "@/integrations/supabase/client";
import { usePlan } from '@/contexts/PlanContext';
import MixpanelService from "@/services/mixpanel";
import * as Sentry from '@sentry/react';

const benefits = [
  "Third-party verified data that builds trust",
  "Real glucose responses that prove your claims",
  "Ready-to-share social content from real users",
  "Dashboard access to all your testimonial assets"
];

const MAX_EXPERIMENT_KITS = 20;
const KIT_PRICE = 99;

// Define form schema with validation
const formSchema = z.object({
  name: z.string().min(1, { message: "Name is required" }),
  email: z.string().email({ message: "Valid email is required" }),
  company: z.string().min(1, { message: "Company is required" }),
  plan: z.string().nullable(),
  experimentKits: z.number().min(0).max(MAX_EXPERIMENT_KITS)
});

type FormValues = z.infer<typeof formSchema>;

const CtaSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { selectedPlan, setSelectedPlan } = usePlan();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [experimentKitCount, setExperimentKitCount] = useState(0);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      plan: selectedPlan,
      experimentKits: 0
    }
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);

      // Track form submission
      MixpanelService.trackAction('Form Submission', {
        form_type: data.plan ? 'checkout' : 'waitlist',
        plan_selected: data.plan,
        experiment_kits: data.experimentKits
      });

      if (data.plan) {
        // Selected a plan - handle payment with Stripe
        console.log(`Processing payment for plan: ${data.plan}`);

        try {
          // Call our edge function to create a checkout session without auth headers
          const { data: checkoutData, error } = await supabase.functions.invoke('create-checkout', {
            body: {
              name: data.name,
              email: data.email,
              company: data.company,
              plan: data.plan,
              experimentKits: data.experimentKits
            },
          });

          if (error) {
            MixpanelService.trackAction('Checkout Error', {
              error_message: error.message
            });
            throw new Error(error.message);
          }

          if (checkoutData?.url) {
            // Track successful checkout creation
            MixpanelService.trackAction('Checkout Created', {
              plan: data.plan,
              experiment_kits: data.experimentKits
            });

            // Redirect to Stripe checkout
            window.location.href = checkoutData.url;
          } else {
            throw new Error("No checkout URL returned");
          }
        } catch (error) {
                      Sentry.captureException(error);
          toast({
            variant: "destructive",
            title: "Checkout Error",
            description: "There was a problem creating your checkout session. Please try again.",
          });
        }
      } else {
        // Join waiting list - record signup
        try {
          const { data: signupData, error } = await supabase.functions.invoke('record-signup', {
            body: {
              name: data.name,
              email: data.email,
              company: data.company,
              isWaitlist: true
            },
          });

          if (error) {
            MixpanelService.trackAction('Waitlist Error', {
              error_message: error.message
            });
            throw new Error(error.message);
          }

          // Track successful waitlist signup
          MixpanelService.trackAction('Waitlist Signup', {
            source: 'cta_section'
          });

          toast({
            title: "You're on the list!",
            description: "Thank you for joining our waiting list.",
          });
          form.reset();
        } catch (error) {
                      Sentry.captureException(error);
          toast({
            variant: "destructive",
            title: "Signup Error",
            description: "There was a problem adding you to the waitlist. Please try again.",
          });
        }
      }
    } catch (error) {
                Sentry.captureException(error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const increaseKits = () => {
    const currentValue = form.getValues('experimentKits');
    if (currentValue < MAX_EXPERIMENT_KITS) {
      form.setValue('experimentKits', currentValue + 1);
      setExperimentKitCount(currentValue + 1);
    }
  };

  const decreaseKits = () => {
    const currentValue = form.getValues('experimentKits');
    if (currentValue > 0) {
      form.setValue('experimentKits', currentValue - 1);
      setExperimentKitCount(currentValue - 1);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-on-scroll');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Update form value when selectedPlan changes
  useEffect(() => {
    form.setValue('plan', selectedPlan);
  }, [selectedPlan, form]);

  // Update selectedPlan when form value changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'plan') {
        setSelectedPlan(value.plan as PlanType);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, setSelectedPlan]);

  const selectedPlanData = selectedPlan ? getSelectedPlan(selectedPlan) : null;

  const getButtonText = () => {
    if (!selectedPlan) {
      return "Join Waiting List";
    }
    return `Purchase Package`;
  };

  // Calculate total with experiment kits
  const calculateTotal = () => {
    let total = 0;

    // Add plan price if selected
    if (selectedPlan) {
      const plan = getSelectedPlan(selectedPlan);
      if (plan) {
        total += plan.price;
      }
    } else {
      // Return 0 if no plan is selected
      return 0;
    }

    // Add experiment kits price
    total += experimentKitCount * KIT_PRICE;

    return total;
  };

  // Filter out the report plan from the pricing tiers for checkout
  const checkoutPricingTiers = pricingTiers.filter(plan => plan.id !== "report");

  return (
    <section ref={sectionRef} className="section-padding bg-white" id="cta-section">
      <div className="container">
        <div className="bg-gradient-to-br from-[#4C1D95] to-[#6D28D9] rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-6 animate-on-scroll">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-5 w-5 text-accent fill-accent" />
                ))}
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-white">
                Prove Your Product Really Works!
              </h2>

              <p className="text-lg text-white">
                Join innovative brands using science to sell.
              </p>

              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3 text-white">
                    <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                      <Check className="h-3 w-3" />
                    </div>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md animate-on-scroll">
              <h3 className="text-xl font-bold mb-4">Get Early Access</h3>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your Name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Work Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="you@company.com"
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Company</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Your Company"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="plan"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              const planValue = value === '' ? null : value as PlanType;
                              field.onChange(planValue);
                              setSelectedPlan(planValue);
                            }}
                            value={field.value || ''}
                            className="space-y-2"
                          >
                            <div
                              className={`flex items-center space-x-2 border ${selectedPlan === null ? 'border-1 border-primary bg-primary-50' : 'border'} rounded-md p-2 hover:bg-neutral-50`}
                            >
                              <RadioGroupItem value="" id="plan-none" />
                              <label htmlFor="plan-none" className="flex flex-col cursor-pointer">
                                <span className="font-medium">I'm Interested</span>
                                <span className="text-xs text-neutral-500">Join our waiting list</span>
                              </label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Experiment Kits Selector */}
                  {selectedPlan && (
                    <FormField
                      control={form.control}
                      name="experimentKits"
                      render={({ field }) => (
                        <FormItem className="pt-4 border-t">
                          <FormLabel className="flex items-center justify-between">
                            <span>Experiment Kits (${KIT_PRICE} each)</span>
                            <span className="text-xs text-neutral-500">Max {MAX_EXPERIMENT_KITS} kits</span>
                          </FormLabel>
                          <div className="bg-neutral-50 p-3 rounded-md mb-2 text-sm text-neutral-700 border border-neutral-100">
                            Add experiment kits to self-test your product. Each kit includes a CGM sensor and testing materials.
                          </div>
                          <div className="flex items-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={decreaseKits}
                              disabled={field.value <= 0}
                              className="h-10 w-10"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <div className="flex-1 text-center">
                              <span className="text-xl font-medium">{field.value}</span> kits
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={increaseKits}
                              disabled={field.value >= MAX_EXPERIMENT_KITS}
                              className="h-10 w-10"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          {field.value > 0 && (
                            <div className="mt-2 text-sm text-right text-neutral-600">
                              ${field.value * KIT_PRICE} added to your order
                            </div>
                          )}
                        </FormItem>
                      )}
                    />
                  )}

                  {/* Total */}
                  {selectedPlan && (selectedPlan || experimentKitCount > 0) && (
                    <div className="pt-4 border-t mt-4">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Total</span>
                        <span className="text-xl font-bold">${calculateTotal()}</span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      getButtonText()
                    )}
                  </Button>

                  <p className="text-xs text-neutral-500 text-center">
                    By signing up, you agree to our terms of service and privacy policy.
                  </p>

                  <p className="text-[10px] text-neutral-400 text-center mt-2">
                    *Purchased credits are eligible for a full refund if our service does not launch by July 14th, 2025.
                  </p>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CtaSection;
